import { parseSearchString } from "@dafiti/core-layout";

import type { CatalogMetaResponse } from "./catalog.type";
import { getRouteSlug, parseCatalogRoute } from "./catalogRoute.helper";
import { buildBrandTitle, buildCategoryTitle } from "./catalogTitle.helper";
import type { Facet } from "./filters.type";

export type CatalogMetadata = {
  title?: string;
  description?: string;
  robots?: string;
};

/** `getTranslations("catalog.metadata")` from next-intl, injected by the page's `generateMetadata`. */
export type CatalogMetadataTranslate = (
  key:
    | "brandDescription"
    | "categoryDescription"
    | "searchTitle"
    | "searchDescription",
  values?: Record<string, string>,
) => string;

/**
 * Builds the <title>/<meta description> for a catalog route.
 *
 * CMS-managed pages keep their curated metadata. For routes without CMS data
 * (notably search pages, whose title/description were previously empty) we
 * generate a structure that mentions the searched term — or the category/brand
 * name — so the page is never shipped with empty SEO tags.
 */
export function getCatalogMetadata(
  pathname: string,
  t: CatalogMetadataTranslate,
  meta?: CatalogMetaResponse,
  facets: Facet[] = [],
): CatalogMetadata {
  const robots = meta?.cms?.meta_robots;

  // CMS-managed pages always take precedence over generated metadata.
  if (meta?.cms?.title) {
    return {
      title: meta.cms.title,
      description: meta.cms.meta_description,
      robots,
    };
  }

  const { kind } = parseCatalogRoute(pathname);
  const slug = getRouteSlug(pathname);

  // Sem `name`, devolve apenas o robots.
  const brandMetadata = (name: string): CatalogMetadata =>
    name
      ? { title: name, description: t("brandDescription", { name }), robots }
      : { robots };

  const categoryMetadata = (name: string): CatalogMetadata =>
    name
      ? { title: name, description: t("categoryDescription", { name }), robots }
      : { robots };

  switch (kind) {
    // Composição "Categoria Marca" dos facets — única fonte, igual ao H1.
    // Sem facets (fetch falhou / catálogo vazio), não há título (só robots).
    case "brand":
      return brandMetadata(buildBrandTitle(facets));

    // Título de categoria: breadcrumb (último crumb não-home) → slug.
    case "category":
      return categoryMetadata(buildCategoryTitle(meta, slug));

    // Busca (/s/) e qualquer rota não reconhecida usam a metadata de busca.
    default: {
      const term = parseSearchString(slug);

      if (!term) return { robots };

      return {
        title: t("searchTitle", { term }),
        description: t("searchDescription", { term }),
        robots,
      };
    }
  }
}
